/**
 * What a member can enter.
 *
 * The member's half of migration 0116, and deliberately a different list
 * from the shop's calendar: this shows only what somebody could actually
 * sign up for, with the price and what it covers beside it.
 *
 * Two decisions worth naming.
 *
 * The price is shown with what it buys, always. "Is the cart included"
 * is the question the shop answers forty times a week in June, and it is
 * answered here or it is answered on the telephone.
 *
 * And a full field offers to join the LIST rather than going grey. A
 * disabled button tells a member nothing except that the software will
 * not help them; the list is what the club actually keeps, and it is
 * what they would have got by ringing.
 */

import { useState } from 'react';
import { api } from '../lib/api.ts';
import { useApi, useMutation } from '../lib/useApi.ts';
import { useSession } from '../lib/session.tsx';
import { Card, ErrorBanner, Money, Status } from './ui.tsx';
import { date as fmtDate } from '../lib/format.ts';

const hhmm = (t: string | null) => (t ? t.slice(0, 5) : '');

export interface OpenEvent {
  id: string;
  name: string;
  kind: string;
  play_date: string;
  course_name: string;
  start_style: string;
  shotgun_at: string | null;
  window_from: string;
  holes: number;
  max_players: number | null;
  notes: string | null;
  entries_open_on: string | null;
  entries_close_on: string | null;
  entry_fee_minor: string | number | null;
  entry_fee_per: string;
  guest_fee_minor: string | number | null;
  fee_includes: string | null;
  players_per_entry: number | null;
  field_size: number;
  waiting: number;
  my_entry_id: string | null;
  my_status: string | null;
  entries_are_open: boolean;
  field_is_full: boolean;
}

export function GolfEvents() {
  const { currency } = useSession();
  const [reload, setReload] = useState(0);
  const q = useApi<{ events: OpenEvent[] }>('/v1/golf/events/open', [reload]);
  const [note, setNote] = useState<string | null>(null);
  const [entering, setEntering] = useState<OpenEvent | null>(null);
  const [guests, setGuests] = useState<string[]>(['']);

  const bump = () => setReload((n) => n + 1);

  const enter = useMutation(async (e: OpenEvent) => {
    const players = guests
      .map((g) => g.trim())
      .filter(Boolean)
      .map((display_name) => ({ player_kind: 'member_guest' as const, display_name }));

    const r = await api<{ on_the_waiting_list: boolean; place: number | null }>(
      `/v1/golf/events/${e.id}/entries`,
      { method: 'POST', body: { players } },
    );

    setEntering(null);
    setGuests(['']);
    setNote(
      r.on_the_waiting_list
        // The number matters. "You are on the list" is a shrug; "third on
        // the list" is something a member can plan around.
        ? `You are on the waiting list for ${e.name} — number ${r.place}. The shop will `
          + 'ring you if a place comes free.'
        : `You are in for ${e.name}.`,
    );
    bump();
  });

  const withdraw = useMutation(async (e: OpenEvent) => {
    await api(`/v1/golf/events/${e.id}/entries/${e.my_entry_id}`, {
      method: 'DELETE', body: { reason: null },
    });
    setNote(`Withdrawn from ${e.name}.`);
    bump();
  });

  const events = q.data?.events ?? [];
  if (q.loading || events.length === 0) return null;

  return (
    <Card title="Events You Can Enter">
      {note && <div className="notice">{note}</div>}
      <ErrorBanner error={q.error ?? enter.error ?? withdraw.error} />

      <ul className="events">
        {events.map((e) => {
          const mine = e.my_entry_id !== null;
          const full = e.field_is_full;

          return (
            <li key={e.id}>
              <div className="events__head">
                <span className="events__name">{e.name}</span>
                {mine && (
                  <Status tone={e.my_status === 'waiting' ? 'warning' : 'good'}>
                    {e.my_status === 'waiting' ? 'On the list' : 'You are in'}
                  </Status>
                )}
              </div>

              <div className="events__when">
                {fmtDate(e.play_date)} · {e.course_name} ·{' '}
                {e.start_style === 'shotgun'
                  ? `shotgun at ${hhmm(e.shotgun_at)}`
                  : `from ${hhmm(e.window_from)}`}
                {e.holes === 9 ? ' · nine holes' : ''}
              </div>

              {/* The price and what it buys, together. Separating them is
                  how a club ends up answering the same question all
                  summer. */}
              {Number(e.entry_fee_minor ?? 0) > 0 && (
                <div className="events__price">
                  <Money minor={e.entry_fee_minor} currency={currency} />
                  {' '}per {e.entry_fee_per === 'player' ? 'player' : 'entry'}
                  {Number(e.guest_fee_minor ?? 0) > 0 && (
                    <> · <Money minor={e.guest_fee_minor} currency={currency} /> per guest</>
                  )}
                  {e.fee_includes && <span className="muted"> — {e.fee_includes}</span>}
                </div>
              )}

              {e.notes && <div className="events__note">{e.notes}</div>}

              <div className="events__meta">
                {e.max_players
                  ? `${e.field_size} of ${e.max_players} places taken`
                  : `${e.field_size} entered`}
                {e.waiting > 0 && ` · ${e.waiting} waiting`}
                {e.entries_close_on && ` · entries close ${fmtDate(e.entries_close_on)}`}
                {!e.entries_are_open && e.entries_open_on
                  && ` · opens ${fmtDate(e.entries_open_on)}`}
              </div>

              <div className="actions" style={{ marginTop: 8 }}>
                {mine ? (
                  <button className="secondary small" disabled={withdraw.busy}
                    onClick={() => withdraw.run(e)}>
                    Withdraw
                  </button>
                ) : !e.entries_are_open ? (
                  <span className="muted small">
                    {e.entries_open_on ? 'Not open yet.' : 'Entries have closed.'}{' '}
                    The shop can still take an entry.
                  </span>
                ) : (
                  <button className="gold small"
                    onClick={() => { setEntering(e); setGuests(['']); }}>
                    {/* A full field offers the list rather than going
                        grey. The list is what the club actually keeps. */}
                    {full ? 'Join the waiting list' : 'Enter'}
                  </button>
                )}
              </div>

              {entering?.id === e.id && (
                <form
                  className="form-rows"
                  style={{ marginTop: 10, maxWidth: 420 }}
                  onSubmit={(ev) => { ev.preventDefault(); enter.run(e); }}
                >
                  {full && (
                    <p className="hint">
                      The field is full. This puts you on the list in the order people
                      applied — the shop rings you if a place comes free.
                    </p>
                  )}

                  {/* Guests only. The member is added by the API without
                      being asked, because somebody entering "me and Ted"
                      means both of them and leaving themselves off their
                      own draw sheet is the mistake worth pre-empting. */}
                  {guests.map((g, i) => (
                    <label className="field" key={i}>
                      <span>{i === 0 ? 'Your guest' : `Guest ${i + 1}`}</span>
                      <input
                        value={g}
                        placeholder={e.players_per_entry === 1 ? 'no guests in this one' : 'Name'}
                        disabled={e.players_per_entry === 1}
                        onChange={(ev) => {
                          const next = [...guests];
                          next[i] = ev.target.value;
                          setGuests(next);
                        }}
                      />
                    </label>
                  ))}

                  {(e.players_per_entry === null
                    || guests.length + 1 < e.players_per_entry) && (
                    <button type="button" className="secondary small"
                      onClick={() => setGuests([...guests, ''])}>
                      Another guest
                    </button>
                  )}

                  <div className="actions">
                    <button className="gold" type="submit" disabled={enter.busy}>
                      {full ? 'Join the list' : 'Enter'}
                    </button>
                    <button className="secondary" type="button"
                      onClick={() => setEntering(null)}>
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
